<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Addendum 14, Modul BM. Sama pola auth-check & dua lapis keamanan dengan
// halaman /platform lain -- lihat komentar di platform/tenants/index.vue.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const STATUS_OPTIONS = [
  { title: 'Menunggu Review', value: 'pending' },
  { title: 'Disetujui', value: 'approved' },
  { title: 'Ditolak', value: 'rejected' },
]

const STATUS_META = {
  pending: { label: 'Menunggu Review', color: 'warning' },
  approved: { label: 'Disetujui', color: 'success' },
  rejected: { label: 'Ditolak', color: 'error' },
}

// ─── State ────────────────────────────────────────────────────────────────
const testimonials = ref([])
const loading = ref(false)
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)
const filterStatus = ref('pending')

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchTestimonials = async () => {
  loading.value = true
  try {
    const res = await usePlatformApi('/api/v1/platform/testimonials', {
      query: { page: page.value, per_page: perPage.value, status: filterStatus.value },
    }).get()

    const body = res?.data ?? res

    testimonials.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err)) return

    console.error('[Platform] fetch testimonials error', err)
    testimonials.value = []
  } finally {
    loading.value = false
  }
}

watch([page, filterStatus], fetchTestimonials, { immediate: true })

// ─── Approve / Reject ───────────────────────────────────────────────────────
const actionLoadingId = ref(null)
const actionError = ref('')

const approveTestimonial = async item => {
  actionLoadingId.value = item.id
  actionError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/testimonials/${item.id}/approve`).post()
    fetchTestimonials()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    actionError.value = data?.message ?? 'Gagal menyetujui testimoni.'
  } finally {
    actionLoadingId.value = null
  }
}

const rejectTestimonial = async item => {
  actionLoadingId.value = item.id
  actionError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/testimonials/${item.id}/reject`).post()
    fetchTestimonials()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    actionError.value = data?.message ?? 'Gagal menolak testimoni.'
  } finally {
    actionLoadingId.value = null
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h4 class="text-h4">
          Review Testimoni
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard>
      <VCardText class="text-body-2 text-medium-emphasis">
        Testimoni yang <strong>Disetujui</strong> langsung tampil di landing page publik.
        Testimoni <strong>Ditolak</strong> tetap tersimpan sebagai arsip internal, tidak
        pernah tampil publik.
      </VCardText>

      <VDivider />

      <VCardText>
        <VAlert
          v-if="actionError"
          type="error"
          variant="tonal"
          density="compact"
          closable
          @click:close="actionError = ''"
        >
          {{ actionError }}
        </VAlert>

        <VRow class="mt-1">
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <VSelect
              v-model="filterStatus"
              :items="STATUS_OPTIONS"
              label="Filter Status"
              hide-details
              density="compact"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="[
          { title: 'Tenant', key: 'tenant', sortable: false },
          { title: 'Rating', key: 'rating', sortable: false, width: 130 },
          { title: 'Ulasan', key: 'message', sortable: false },
          { title: 'Status', key: 'status', sortable: false },
          { title: 'Dikirim', key: 'created_at', sortable: false },
          { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 180 },
        ]"
        :items="testimonials"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Tidak ada testimoni dengan status ini."
      >
        <template #item.tenant="{ item }">
          {{ item.tenant?.name ?? '-' }}
          <div class="text-caption text-medium-emphasis">
            {{ item.submitted_by?.name ?? '-' }}
          </div>
        </template>

        <template #item.rating="{ item }">
          <div class="d-flex">
            <VIcon
              v-for="i in 5"
              :key="i"
              icon="ri-star-fill"
              size="16"
              :color="i <= item.rating ? 'warning' : 'grey-lighten-2'"
            />
          </div>
        </template>

        <template #item.message="{ item }">
          <span
            class="text-body-2"
            style="max-inline-size: 360px; display: inline-block;"
          >
            {{ item.message }}
          </span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="STATUS_META[item.status]?.color"
            variant="tonal"
            size="small"
          >
            {{ STATUS_META[item.status]?.label ?? item.status }}
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.actions="{ item }">
          <div
            v-if="item.status === 'pending'"
            class="d-flex gap-1 justify-center"
          >
            <VBtn
              color="success"
              size="small"
              variant="tonal"
              :loading="actionLoadingId === item.id"
              @click="approveTestimonial(item)"
            >
              Approve
            </VBtn>
            <VBtn
              color="error"
              size="small"
              variant="tonal"
              :loading="actionLoadingId === item.id"
              @click="rejectTestimonial(item)"
            >
              Tolak
            </VBtn>
          </div>
          <span
            v-else
            class="text-caption text-medium-emphasis"
          >
            {{ item.status === 'approved' ? `Disetujui ${formatDate(item.approved_at)}` : 'Sudah ditolak' }}
          </span>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>
